import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DateRangePicker from '~components/Filters/DateRangePicker';
import { Ticket } from '~hooks/useTickets/models';
import DeletedTicketImageCell, {
  MissingImagePlaceholder,
} from '~pages/Settings/DeletedRecords/components/DeletedTicketImageCell';
import useDeletedRecordsCursor from '~pages/Settings/DeletedRecords/useDeletedRecordsCursor';
import useDeletedRecordsDateFilter from '~pages/Settings/DeletedRecords/useDeletedRecordsDateFilter';
import useDiscardedTicketsQuery from '~queries/useDiscardedTicketsQuery';
import { snakeCaseToHuman } from '~utils/utilFunctions';

// Fits the 50px ticket thumbnail plus the cell's horizontal padding.
const IMAGE_COLUMN_WIDTH_PX = 80;

interface DeletedTicketsTabProps {
  companyId: string;
}

export default function DeletedTicketsTab({ companyId }: DeletedTicketsTabProps) {
  const { appliedRange, applyRange, draftRange, selectRange } =
    useDeletedRecordsDateFilter();

  // to_date is an exclusive midnight bound, so the picked end day needs the next date.
  const fromDateParam = appliedRange.startDate?.format('YYYY-MM-DD');
  const toDateParam = appliedRange.endDate?.add(1, 'day').format('YYYY-MM-DD');

  const filtersKey = JSON.stringify([companyId, fromDateParam, toDateParam]);
  const { cursor, goToPage, paginationForDisplay } = useDeletedRecordsCursor(filtersKey);

  const { isFetching, pagination, tickets } = useDiscardedTicketsQuery({
    companyId,
    cursor: cursor.cursor,
    direction: cursor.direction,
    fromDate: fromDateParam,
    toDate: toDateParam,
  });

  const columns = useMemo<Array<DataTableColumn<Ticket>>>(
    () => [
      {
        field: 'ticketNumber',
        headerName: t('settings.deleted_records.columns.ticket_number'),
        valueGetter: (ticket) => ticket.ticketNumber,
      },
      {
        field: 'friendlyJobId',
        headerName: t('settings.deleted_records.columns.trd_id'),
        valueGetter: (ticket) => ticket.friendlyJobId,
      },
      {
        field: 'image',
        headerName: t('settings.deleted_records.columns.image'),
        maxWidth: IMAGE_COLUMN_WIDTH_PX,
        minWidth: IMAGE_COLUMN_WIDTH_PX,
        // previewUrl falls back to the full image, so an absent previewUrl means the
        // ticket genuinely has no image (paper or manually entered).
        renderCell: (ticket) =>
          ticket.previewUrl ? (
            <DeletedTicketImageCell
              imageMimeType={ticket.imageMimeType}
              imageUrl={ticket.imageUrl}
              previewUrl={ticket.previewUrl}
              ticketNumber={ticket.ticketNumber}
            />
          ) : (
            <MissingImagePlaceholder />
          ),
      },
      {
        field: 'quantity',
        headerName: t('form_fields.quantity'),
        valueGetter: (ticket) => {
          if (!ticket.quantity) {
            return '';
          }

          const unit = ticket.unitOfMeasure?.name;

          return unit ? `${ticket.quantity} ${unit}` : ticket.quantity;
        },
      },
      {
        field: 'serviceDate',
        headerName: t('settings.deleted_records.columns.service_date'),
        valueGetter: (ticket) =>
          ticket.serviceDate ? dayjs.tz(ticket.serviceDate).format('MM/DD/YYYY') : '',
      },
      {
        field: 'source',
        headerName: t('form_fields.source'),
        valueGetter: (ticket) => (ticket.source ? snakeCaseToHuman(ticket.source) : ''),
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        {/* Labelled for tickets.created_at — not the deletion date. */}
        <Typography sx={{ color: 'text.secondary', fontSize: '12px' }}>
          {t('settings.deleted_records.filters.ticket_created_date')}
        </Typography>
        <DateRangePicker
          allowAllDates
          showShortcuts
          endDate={draftRange.endDate}
          startDate={draftRange.startDate}
          onAnchorClose={applyRange}
          onDateSelect={selectRange}
        />
      </Box>

      <DataTable
        id="deleted-records-tickets-grid"
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            pagination={paginationForDisplay(pagination)}
            onNavigate={(direction) => goToPage(direction, pagination)}
          />
        }
        loading={isFetching}
        noRowsOverlayText={t('settings.deleted_records.no_deleted_tickets')}
        rows={tickets}
      />
    </Box>
  );
}
